import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../utils/firebase";
import { useAuth } from "../../../context/AuthContext";
import { logActivity } from "../activityLog";
import { toast } from "react-toastify";

type DeployEnvFields = Array<{ key: string; value: string }>;
interface DeployState { dev: DeployEnvFields; prod: DeployEnvFields; }
interface Category { id: string; name: Record<string, string>; }

const LANG = "en";

const toDeployFields = (obj: any): DeployEnvFields =>
  Object.entries(obj || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => ({ key, value: String(value) }));

const fromDeployFields = (fields: DeployEnvFields): Record<string, string> =>
  Object.fromEntries(fields.map((f) => [f.key, f.value]));

const GeneralSettings: React.FC = () => {
  const { project } = useParams<{ project: string }>();
  const { user, firebaseUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deploy, setDeploy] = useState<DeployState>({ dev: [], prod: [] });
  const [categories, setCategories] = useState<Category[]>([]);
  const [newCategoryName, setNewCategoryName] = useState("");

  useEffect(() => {
    if (!project) return;
    setLoading(true);
    Promise.all([
      getDoc(doc(db, "content", project, "_config", "deploy")),
      getDoc(doc(db, "content", project, "_config", "insights")),
    ])
      .then(([deploySnap, insightsSnap]) => {
        if (deploySnap.exists()) {
          const d = deploySnap.data();
          setDeploy({ dev: toDeployFields(d.dev), prod: toDeployFields(d.prod) });
        }
        if (insightsSnap.exists()) {
          const raw = insightsSnap.data().categories || [];
          // migrate old string[] format
          const cats: Category[] = raw.map((c: any) =>
            typeof c === "string" ? { id: c.toLowerCase().replace(/[^a-z0-9]+/g, "-"), name: { [LANG]: c } } : c
          );
          setCategories(cats);
        }
      })
      .catch((err) => console.error("[GeneralSettings] Load error:", err))
      .finally(() => setLoading(false));
  }, [project]);

  const save = async () => {
    if (!project) return;
    setSaving(true);
    try {
      await Promise.all([
        setDoc(doc(db, "content", project, "_config", "deploy"), {
          dev: fromDeployFields(deploy.dev),
          prod: fromDeployFields(deploy.prod),
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || "unknown",
        }),
        setDoc(doc(db, "content", project, "_config", "insights"), {
          categories,
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || "unknown",
        }),
      ]);
      toast.success("Saved");
      logActivity(project, {
        action: "edit",
        contentType: "general",
        docTitle: "General Settings",
        user: user?.email || firebaseUser?.email || "unknown",
        photoURL: user?.photoURL || firebaseUser?.photoURL || undefined,
      });
    } catch (err) {
      console.error("[GeneralSettings] Save error:", err);
      toast.error("Save failed");
    }
    setSaving(false);
  };

  const updateDeployField = (env: "dev" | "prod", index: number, field: "key" | "value", val: string) => {
    setDeploy((prev) => {
      const next = { ...prev, [env]: [...prev[env]] };
      next[env][index] = { ...next[env][index], [field]: val };
      return next;
    });
  };

  const addDeployField = (env: "dev" | "prod") => {
    setDeploy((prev) => ({ ...prev, [env]: [...prev[env], { key: "", value: "" }] }));
  };

  const removeDeployField = (env: "dev" | "prod", index: number) => {
    setDeploy((prev) => ({ ...prev, [env]: prev[env].filter((_, i) => i !== index) }));
  };

  const addCategory = () => {
    const val = newCategoryName.trim();
    if (!val) return;
    const id = val.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    if (categories.some((c) => c.id === id)) return;
    setCategories([...categories, { id, name: { [LANG]: val } }]);
    setNewCategoryName("");
  };

  const removeCategory = (index: number) => {
    setCategories(categories.filter((_, i) => i !== index));
  };

  if (loading) return <div className="editor__loading">Loading...</div>;

  return (
    <div className="general">
      <div className="general__header">
        <h2 className="general__title">General Settings</h2>
        <div className="general__actions">
          <button className="editor__save-btn" onClick={save} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <div className="general__section">
        <h3 className="general__section-title">Insight Categories</h3>
        <div className="general__categories">
          {categories.map((cat, i) => (
            <div key={cat.id} className="general__category-item">
              <span className="general__category-id">{cat.id}</span>
              <span className="general__category-name">{cat.name[LANG] || cat.id}</span>
              <button className="general__remove-field" onClick={() => removeCategory(i)} title="Remove">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          ))}
          <div className="general__category-add">
            <input
              className="block-input"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCategory()}
              placeholder="Category name (EN)"
            />
            <button className="general__add-deploy-field" onClick={addCategory}>+ Add</button>
          </div>
        </div>
      </div>

      <div className="general__deploy">
        <h3 className="general__section-title">Deploy Config</h3>
        {(["dev", "prod"] as const).map((env) => (
          <div key={env} className="general__deploy-env">
            <div className="general__deploy-env-label">{env === "dev" ? "Dev / Stage" : "Production"}</div>
            {deploy[env].map((row, i) => (
              <div key={i} className="general__deploy-row">
                <input
                  className="block-input block-input--small"
                  value={row.key}
                  onChange={(e) => updateDeployField(env, i, "key", e.target.value)}
                  placeholder="key"
                />
                <input
                  className="block-input"
                  value={row.value}
                  onChange={(e) => updateDeployField(env, i, "value", e.target.value)}
                  placeholder="value"
                />
                <button className="general__remove-field" onClick={() => removeDeployField(env, i)} title="Remove">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            ))}
            <button className="general__add-deploy-field" onClick={() => addDeployField(env)}>+ Add field</button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GeneralSettings;
